import { Component, Input, OnInit } from '@angular/core';
import { FipePrice } from '@app-model/vehicle';
import { OpenService } from '@app-services/open.service';
import { SessionService } from '@app/store/session/session.service';
import { ChartConfiguration } from "chart.js";


@Component({
  selector: 'app-dash-fipe',
  templateUrl: './dash-fipe.component.html',
  styleUrls: ['./dash-fipe.component.scss']
})
export class DashFipeComponent implements OnInit {


  @Input('oldFipes') oldFipes: FipePrice[];
  @Input('currentFipe') currentFipe: number;
  @Input('futureFipe') futureFipe?: number;
  @Input('evaluation') isEval: boolean;

  fipePrices: FipePrice[];

  backgroundColor = '#16b097';


  lineChartLabels: string[] = [];
  lineChartData: any[];
  lineChartType: ChartConfiguration<'line'>['type'] = 'line';
  lineChartOptions: any;
  lineChartLegend = false;

  constructor(
    private sessionService: SessionService,
    private openService: OpenService
  ) { }

  ngOnInit() {
    // Get the color from CSS variable --secondary-button-color
    const computedColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-button-color').trim();
    this.backgroundColor = computedColor || '#16b097';
    
    this.sessionService.getUser().subscribe(user => {
      this.lineChartData = this.createLineChartDataConfig();
      this.lineChartOptions = this.createLineChartOptions();
      this.initFipes();
      this.updateChart();
    });
  }

  getMonthYear(date: Date) {
    date = new Date(date);
    const options = { month: 'short', year: 'numeric' } as Intl.DateTimeFormatOptions;
    return date.toLocaleDateString('pt-BR', options).replace(". de ", "/").toUpperCase();
  }

  initFipes(): void {
    this.fipePrices = this.oldFipes;

    if (!this.fipePrices) {
      this.fipePrices = [];
    }

    if (this.currentFipe && !this.alreadyHasCurrentFipe()) {
      this.fipePrices.push(new FipePrice(new Date(), this.currentFipe));
    }

    if (this.futureFipe) {
      let newDate = new Date();
      newDate.setMonth(newDate.getMonth() + 1);
      this.fipePrices.push(new FipePrice(newDate, this.futureFipe));
    }
  }

  private alreadyHasCurrentFipe(): boolean {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return this.fipePrices.some((fipePrice: FipePrice) => {
      const date = new Date(fipePrice.indexMonth as any);
      if (Number.isNaN(date.getTime())) {
        return false;
      }
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
  }

  updateChart() {
    this.fipePrices.forEach((fipePrice: FipePrice) => {
      this.lineChartLabels.push(this.getMonthYear(fipePrice.indexMonth));
      this.lineChartData[0].data.push(fipePrice.price);
    });

    if (this.isEval) {
      const menuItemColor = getComputedStyle(document.documentElement).getPropertyValue('--menu-item-color').trim() || '#cbced1';
      this.lineChartOptions.scales = this.lineChartOptions.scales || {};
      this.lineChartOptions.scales.y = this.lineChartOptions.scales.y || {};
      this.lineChartOptions.scales.y.ticks = this.lineChartOptions.scales.y.ticks || {};
      this.lineChartOptions.scales.y.ticks.color = menuItemColor;

      this.lineChartOptions.scales.x = this.lineChartOptions.scales.x || {};
      this.lineChartOptions.scales.x.ticks = this.lineChartOptions.scales.x.ticks || {};
      this.lineChartOptions.scales.x.ticks.color = menuItemColor;
    }
  }

  createLineChartOptions(): any {
    const menuItemColor = getComputedStyle(document.documentElement).getPropertyValue('--menu-item-color').trim() || '#919a9e';
    return {
      responsive: true,
      scales: {
        // Chart.js 3+ (usado efetivamente)
        y: {
          ticks: {
            maxTicksLimit: 8,
            stepSize: 500,
            color: menuItemColor,
            callback: (value: any) => 'R$ ' + Number(value).toLocaleString('pt-BR')
          }
        },
        x: {
          grid: { display: false },
          ticks: {
            color: menuItemColor
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: any) => 'R$ ' + Number(ctx.parsed?.y ?? ctx.raw).toLocaleString('pt-BR')
          },
          backgroundColor: this.backgroundColor,
          displayColors: false
        },
        datalabels: {
          display: false,
        }
      }
    };
  }

  private createLineChartDataConfig(): any[] {
    return [
      {
        data: [],
        fill: true,
        tension: 0.1,
        borderColor: this.backgroundColor,
        borderWidth: 2,
        backgroundColor: 'transparent',
        pointBackgroundColor: '#fff',
        pointBorderColor: this.backgroundColor,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverBackgroundColor: this.backgroundColor,
        pointHoverBorderColor: this.backgroundColor
      }
    ];
  }
}